import { Form, Spin } from "antd";
import { useApp } from "context/AppContext";
import { guestLoginForOrderId, searchPassportByOrderId } from "pages/SignUp/Api";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TITLE } from "../../constants";
import ContactStep from "./Components/ContactStep";
import OtpStep from "./Components/OtpStep";
import PassportStep from "./Components/PassportStep";
import Title from "./Components/Title";
import useIsMobile from "pages/EDCFlow/hooks/useIsMobile";

const SignupForm: React.FC = () => {
	const [form] = Form.useForm<SignupFormValues>();
	const { orderId } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { setPassportDetails } = useApp();
	const step = Form.useWatch("step", form);
	const phone = Form.useWatch("phone", { form, preserve: true });
	const isMobile = useIsMobile();
	const location = useLocation();
	useEffect(() => {
		if (!isMobile) return;
		if (!orderId) return;

		if (location.pathname.includes("/signup")) {
			navigate(`/questionnaire/${orderId}${location.search}`, {
				replace: true
			});
		}
	}, [isMobile, orderId, location.pathname, location.search, navigate]);

	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const { data } = await guestLoginForOrderId(orderId);
				sessionStorage.setItem("jwt-token", data.data.token);
				form.setFieldValue("step", "passport");

				const passportFromUrl = searchParams.get("pid");
				if (passportFromUrl && orderId) {
					try {
						const { data: passportData } = await searchPassportByOrderId(orderId, passportFromUrl);
						setPassportDetails(passportData.data);
						const applicationId = passportData.data.application_id;
						navigate(`/questionnaire/${orderId}?application_id=${applicationId}`);
						return;
					} catch (passportErr) {
						console.error("Passport search failed:", passportErr);
					}
				}
			} catch (err) {
				form.setFieldValue("step", "phone");
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [form, orderId, searchParams, setPassportDetails, navigate]);

	return (
		<Form
			className="relative h-full w-full flex justify-center items-center row-span-2"
			form={form}
			preserve
			layout="vertical"
			initialValues={{ step: "phone" }}>
			<Form.Item name="step" className="hidden">
				<div role="radiogroup">
					<input hidden type="radio" name="step" value="phone" defaultChecked />
					<input hidden type="radio" name="step" value="otp" />
					<input hidden type="radio" name="step" value="passport" />
				</div>
			</Form.Item>
			{loading ? (
				<div className="flex flex-col items-center gap-4">
					<Spin size="large" />
					<p className="text-gray-600">Processing, please wait…</p>
				</div>
			) : (
				<div className="grid place-content-center grid-cols-1 gap-5 md:gap-10 self-end md:self-center">
					<h3 className=" text-lg md:text-2xl text-gray-900 font-semibold text-center px-5">
						Your travel agent is inviting you to <span className="text-primary-700">collaborate</span> on
						your travel questionnaire!
						<p className="hidden md:block text-lg text-gray-700 font-normal mt-1">
							Please login with your registered contact number to access your questionnaire
						</p>
					</h3>

					<div className="self-center block md:hidden">
						{step && (
							<Title
								svg={TITLE[step].svg}
								heading={TITLE[step].heading}
								description={TITLE[step].description}
								descriptionAddon={
									step === "otp" ? <span className="text-gray-800">+91 {phone}</span> : undefined
								}
							/>
						)}
					</div>

					<section className="self-end md:self-center max-w-md flex-1 md:mx-auto overflow-hidden bg-white shadow-md ring-1 ring-gray-200 rounded-xl mb-8 mx-2.5 md:mb-0 md:w-full">
						{step && (
							<>
								<div className="hidden md:block">
									<Title
										svg={TITLE[step].svg}
										heading={TITLE[step].heading}
										description={TITLE[step].description}
										descriptionAddon={
											step === "otp" ? (
												<span className="text-gray-800">+91 {phone}</span>
											) : undefined
										}
									/>
								</div>

								{(() => {
									switch (step) {
										case "otp":
											return <OtpStep />;
										case "passport":
											return <PassportStep />;
										default:
											return <ContactStep />;
									}
								})()}
							</>
						)}
					</section>
				</div>
			)}
		</Form>
	);
};

export default SignupForm;

